import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useCamera Hook
 * Provides camera access and photo capture functionality
 */
export function useCamera(options = {}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [facingMode, setFacingMode] = useState(options.facingMode || 'environment');

  // Start camera
  const startCamera = useCallback(async (requestedFacingMode) => {
    try {
      setError(null);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera is not supported by this browser');
      }

      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const currentFacingMode = requestedFacingMode || facingMode;

      const constraints = {
        video: {
          facingMode: currentFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          ...options.video
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Wait for video to be ready
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            resolve();
          };
        });
        await videoRef.current.play();
      }

      setIsActive(true);
      if (requestedFacingMode) {
        setFacingMode(requestedFacingMode);
      }
    } catch (err) {
      let errorMessage;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera permission denied. Please enable camera access in your browser settings.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not support the requested settings.';
      } else {
        errorMessage = err.message || 'Failed to access camera.';
      }
      const error = new Error(errorMessage);
      error.name = err.name;
      setError(error);
      throw error;
    }
  }, [facingMode, options.video]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsActive(false);
  }, []);

  // Capture photo
  const capturePhoto = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!videoRef.current || !canvasRef.current) {
        reject(new Error('Camera not initialized'));
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // Convert to blob
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to capture photo'));
          return;
        }

        const photoData = {
          blob,
          dataUrl: canvas.toDataURL('image/jpeg', 0.85),
          width: canvas.width,
          height: canvas.height,
          timestamp: new Date()
        };
        setPhoto(photoData);
        resolve(photoData);
      }, 'image/jpeg', 0.85);
    });
  }, []);

  // Toggle camera facing mode
  const toggleFacingMode = useCallback(async () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';

    if (isActive) {
      stopCamera();
      // Small delay for camera to release
      await new Promise(r => setTimeout(r, 300));
      await startCamera(newMode);
    } else {
      setFacingMode(newMode);
    }
  }, [facingMode, isActive, stopCamera, startCamera]);

  // Clear captured photo
  const clearPhoto = useCallback(() => {
    setPhoto(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return {
    videoRef,
    canvasRef,
    isActive,
    error,
    photo,
    facingMode,
    startCamera,
    stopCamera,
    capturePhoto,
    toggleFacingMode,
    clearPhoto,
    clearError,
    isSupported: typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      !!navigator.mediaDevices.getUserMedia
  };
}

export default useCamera;
